import { css } from 'lit';

export const styles = css`
  :host {
    display: block;
  }

  /* ─── Base layout ─────────────────────────────────────────── */
  [part='base'] {
    display: flex;
    flex-direction: column;
    gap: var(--asc-spacing-sm);
  }

  /* ─── Label row ──────────────────────────────────────────── */
  [part='label-row'] {
    display: flex;
    align-items: center;
    gap: var(--asc-spacing-xs);
  }

  [part='label-row'][hidden] {
    display: none;
  }

  [part='label'] {
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-select-label-font-size, var(--asc-font-size-text-sm));
    line-height: var(--asc-line-height-text-sm);
    font-weight: var(--asc-select-label-font-weight, var(--asc-font-weight-medium));
    color: var(--asc-select-label-color, var(--asc-text-secondary));
  }

  [part='required-indicator'] {
    color: var(--asc-select-required-color, var(--asc-text-brand-tertiary));
    font-size: var(--asc-select-label-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-regular);
    line-height: 1;
  }

  [part='help-icon'] {
    display: flex;
    align-items: center;
    color: var(--asc-fg-quaternary);
    cursor: help;
  }

  /* ─── Trigger ────────────────────────────────────────────── */
  /*
   * Figma: bg-primary, 1px border-primary, radius-md (12px), shadow-xs.
   * Focused/open: 2px border-brand (#008577).
   */
  [part='trigger'] {
    /* Reset */
    -webkit-appearance: none;
    appearance: none;
    background: none;
    font: inherit;
    cursor: pointer;
    text-align: start;

    /* Layout */
    display: flex;
    align-items: center;
    gap: var(--asc-spacing-md);
    width: 100%;
    box-sizing: border-box;

    /* Visual */
    background-color: var(--asc-select-trigger-bg, var(--asc-bg-primary));
    border: var(--asc-border-width, 1px) solid
      var(--asc-select-trigger-border-color, var(--asc-border-primary));
    border-radius: var(--asc-select-trigger-radius, var(--asc-radius-md));
    box-shadow: var(--asc-select-trigger-shadow, 0px 1px 2px 0px var(--asc-shadow-xs));
    padding:
      var(--asc-select-trigger-padding-y, var(--asc-spacing-md))
      var(--asc-select-trigger-padding-x, var(--asc-spacing-lg));

    /* Typography */
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-font-size-text-sm);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-text-primary);

    /* Transition */
    transition:
      border-color var(--asc-transition-fast),
      box-shadow var(--asc-transition-fast);
  }

  /* Focus: 2px brand border + focus ring */
  [part='trigger']:focus-visible {
    outline: none;
    border-width: 2px;
    border-color: var(--asc-select-trigger-border-color-focus, var(--asc-border-brand));
    box-shadow:
      var(--asc-select-trigger-shadow, 0px 1px 2px 0px var(--asc-shadow-xs)),
      0 0 0 2px var(--asc-bg-primary),
      0 0 0 4px var(--asc-focus-ring);
  }

  /* Open state: 2px brand border */
  :host([open]) [part='trigger'] {
    border-width: 2px;
    border-color: var(--asc-select-trigger-border-color-focus, var(--asc-border-brand));
  }

  /* Disabled */
  :host([disabled]) [part='trigger'] {
    background-color: var(--asc-select-trigger-bg-disabled, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-select-trigger-border-color-disabled, var(--asc-border-disabled));
    cursor: not-allowed;
    pointer-events: none;
  }

  /* Size=md */
  :host([size='md']) [part='trigger'] {
    font-size: var(--asc-font-size-text-md);
    line-height: var(--asc-line-height-text-md);
    padding:
      var(--asc-spacing-xl)
      var(--asc-select-trigger-padding-x, var(--asc-spacing-lg));
  }

  /* ─── Prefix (leading icon/avatar/dot slot) ───────────────── */
  [part='prefix'] {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  [part='prefix'][hidden] {
    display: none;
  }

  /* ─── Search icon (search and tags types) ────────────────── */
  [part='search-icon'] {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    color: var(--asc-fg-tertiary);
  }

  /* div-based trigger (search/tags): flex-start so the search icon pins to the
     first row; height grows naturally as chips wrap to additional rows. */
  div[part='trigger'] {
    align-items: flex-start;
    overflow: visible;
  }

  /* ─── Tags + input area (tags type) ──────────────────────── */
  [part='tags-input-area'] {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--asc-spacing-xs);
    flex: 1;
    min-width: 0;
    overflow: visible;
  }

  /* ─── Display value / placeholder text ────────────────────── */
  [part='display-value'] {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow-wrap: break-word;
    min-width: 0;
    color: var(--asc-select-value-color, var(--asc-text-primary));
    font-weight: var(--asc-select-value-font-weight, var(--asc-font-weight-medium));
  }

  [part='placeholder-text'] {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow-wrap: break-word;
    min-width: 0;
    color: var(--asc-select-placeholder-color, var(--asc-text-placeholder));
    font-weight: var(--asc-font-weight-regular);
  }

  :host([disabled]) [part='display-value'],
  :host([disabled]) [part='placeholder-text'] {
    color: var(--asc-select-text-disabled, var(--asc-text-disabled));
  }

  /* ─── Chevron ─────────────────────────────────────────────── */
  [part='chevron'] {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: auto;
    color: var(--asc-fg-tertiary);
    width: var(--asc-select-chevron-size, var(--asc-spacing-lg));
    height: var(--asc-select-chevron-size, var(--asc-spacing-lg));
    transition: transform var(--asc-transition-fast);
  }

  :host([open]) [part='chevron'] {
    transform: rotate(180deg);
  }

  /* ─── Search input (search and tags types) ────────────────── */
  [part='search-input'] {
    flex: 1;
    /* Force the input to wrap to a new row in tags mode when chips leave
       insufficient space — the wrap threshold must exceed a typical chip width. */
    min-width: 160px;
    border: none;
    outline: none;
    background: transparent;
    padding: 0;
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-font-size-text-sm);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-select-search-color, var(--asc-text-primary));
  }

  [part='search-input']::placeholder {
    color: var(--asc-select-placeholder-color, var(--asc-text-placeholder));
    font-weight: var(--asc-font-weight-regular);
  }

  :host([disabled]) [part='search-input'] {
    color: var(--asc-select-text-disabled, var(--asc-text-disabled));
  }

  :host([size='md']) [part='search-input'] {
    font-size: var(--asc-font-size-text-md);
    line-height: var(--asc-line-height-text-md);
  }

  /* ─── Tag chip ────────────────────────────────────────────── */
  [part='tag'] {
    display: inline-flex;
    align-items: center;
    gap: 3px; /* no-token: Figma specifies 3px gap between content and X */
    padding: 2px 4px 2px 5px; /* no-token: Figma tag padding pl-5 pr-4 py-2 */
    background-color: var(--asc-select-tag-bg, var(--asc-bg-primary));
    border: 1px solid var(--asc-select-tag-border-color, var(--asc-border-primary));
    border-radius: var(--asc-select-tag-radius, var(--asc-radius-sm));
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* ─── Tag inner content row (avatar + label) ──────────────── */
  [part='tag-content'] {
    display: flex;
    align-items: center;
    gap: 5px; /* no-token: Figma specifies 5px gap between avatar and label */
    flex-shrink: 0;
  }

  /* ─── Tag avatar ──────────────────────────────────────────── */
  [part='tag-avatar'] {
    width: 16px;
    height: 16px;
    border-radius: var(--asc-radius-full);
    border: 0.667px solid var(--asc-border-secondary);
    object-fit: cover;
    flex-shrink: 0;
    display: block;
  }

  /* ─── Tag label text ──────────────────────────────────────── */
  [part='tag-content'] span {
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-font-size-text-sm);
    font-weight: var(--asc-font-weight-medium);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-select-tag-color, var(--asc-text-secondary));
  }

  [part='tag-remove'] {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: var(--asc-fg-tertiary);
    line-height: 1;
    border-radius: 3px; /* no-token: Figma specifies 3px radius on X button */
    flex-shrink: 0;
  }

  [part='tag-remove']:hover {
    color: var(--asc-fg-secondary);
  }

  [part='tag-remove']:focus-visible {
    outline: 2px solid var(--asc-focus-ring);
    outline-offset: 1px;
  }

  /* ─── Trigger wrapper (positioning context for dropdown) ─── */
  [part='trigger-wrapper'] {
    position: relative;
  }

  /* ─── Panel (dropdown container) ─────────────────────────── */
  /*
   * Figma: bg-primary, 1px border-secondary (#a5b8bf), radius-md (12px),
   * shadow-lg (multi-layer), max-height 256px, 4px vertical padding inside menu.
   * Positioned absolutely below the trigger.
   */
  [part='panel'] {
    display: none;
    position: absolute;
    top: calc(100% + var(--asc-spacing-xs));
    left: 0;
    right: 0;
    z-index: var(--asc-z-popover);
    background-color: var(--asc-select-menu-bg, var(--asc-bg-primary));
    border: 1px solid var(--asc-select-menu-border-color, var(--asc-border-secondary));
    border-radius: var(--asc-select-menu-radius, var(--asc-radius-md));
    box-shadow: var(
      --asc-select-menu-shadow,
      0 12px 16px -4px var(--asc-shadow-lg_01),
      0 4px 6px -2px var(--asc-shadow-lg_02),
      0 2px 2px -1px var(--asc-shadow-lg_03)
    );
    overflow: hidden;
  }

  :host([open]) [part='panel'] {
    display: block;
  }

  /* ─── Menu (listbox) ──────────────────────────────────────── */
  [part='menu'] {
    list-style: none;
    margin: 0;
    padding: var(--asc-spacing-xs) 0;
    max-height: var(--asc-select-menu-max-height, 256px);
    overflow-y: auto;
  }

  /* ─── Menu item ───────────────────────────────────────────── */
  /*
   * Figma: 8px padding (spacing-md), border-radius spacing-sm (6px).
   * Note: spacing-sm (6px) used for item radius — no matching --asc-radius-* at 6px.
   */
  [part~='menu-item'] {
    display: flex;
    align-items: center;
    gap: var(--asc-spacing-md);
    padding: var(--asc-select-item-padding, var(--asc-spacing-md));
    border-radius: var(--asc-select-item-radius, var(--asc-spacing-sm));
    margin: 0 var(--asc-spacing-xs);
    cursor: pointer;
    outline: none;
    user-select: none;
    transition: background-color var(--asc-transition-fast);
  }

  [part~='menu-item'][hidden] {
    display: none;
  }

  [part~='menu-item']:hover {
    background-color: var(--asc-select-item-bg-hover, var(--asc-bg-primary_hover));
  }

  /* Focus ring only during keyboard navigation — prevents gray bg on mouse-opened panel */
  [part~='menu-item']:focus-visible {
    background-color: var(--asc-select-item-bg-hover, var(--asc-bg-primary_hover));
    box-shadow: inset 0 0 0 2px var(--asc-focus-ring);
  }

  /* Suppress the browser default :focus outline — we handle it via :focus-visible above */
  [part~='menu-item']:focus {
    outline: none;
  }

  [part~='menu-item--selected'] {
    background-color: var(--asc-select-item-bg-selected, var(--asc-bg-active));
  }

  [part~='menu-item--selected']:hover {
    background-color: var(--asc-select-item-bg-selected, var(--asc-bg-active));
  }

  /* Focus ring on selected item only during keyboard navigation — no green ring on hover */
  [part~='menu-item--selected']:focus-visible {
    background-color: var(--asc-select-item-bg-selected, var(--asc-bg-active));
    box-shadow: inset 0 0 0 2px var(--asc-focus-ring);
  }

  /* ─── Menu item check icon ────────────────────────────────── */
  [part='menu-item-check'] {
    display: flex;
    align-items: center;
    margin-left: auto;
    flex-shrink: 0;
    color: var(--asc-fg-brand-primary);
  }

  /* ─── Empty state (no results after filtering) ───────────── */
  [part='menu-empty'] {
    padding: var(--asc-spacing-md) var(--asc-spacing-lg);
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-font-size-text-sm);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-select-empty-color, var(--asc-text-tertiary));
    text-align: center;
  }

  /* ─── Default slot (option source elements) ────────────────
   * Kept in light DOM for SEO crawlability but hidden from visual rendering.
   * Using ::slotted() applies the rule directly to the distributed light DOM
   * elements, which is the only reliable way to hide them. Styling the <slot>
   * element itself (slot:not([name])) does not suppress slotted content because
   * distributed elements escape the slot's clip context in the flat tree.
   * These adoptedStyleSheets styles are not subject to the page CSP style-src.
   * ─────────────────────────────────────────────────────────── */
  ::slotted(:not([slot])) {
    display: none !important;
  }

  /* ─── Hint text ───────────────────────────────────────────── */
  [part='hint-text'] {    font-family: var(--asc-font-family-body);
    font-size: var(--asc-select-hint-font-size, var(--asc-font-size-text-sm));
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-select-hint-color, var(--asc-text-tertiary));
  }

  [part='hint-text'][hidden] {
    display: none;
  }
`;
